<template>
    <div class="forms__field--repeater__wrapper">
        <input
            :id="props.id"
            type="text"
            class="sr-only"
            :tabindex="props.tabindex"
            @focusin="handleLabelFocus"
        />
        <div class="forms__field--repeater__list" :data-repeater-list="field.slug || 'root'">
            <component
                :is="layoutComponent"
                ref="layoutRef"
                :field="field"
                :items="internalModelValue"
                :tabindex="tabindex"
                :multiple="multiple"
                :inline="inline"
                :size="props.size"
                :label-position="props.labelPosition"
                :minus-icon="props.minusIcon"
                @remove="removeFromRepeater"
                @sort-end="end"
                @sort-add="add"
            >
                <template #subfield-after="slotProps">
                    <slot :name="`repeater-after-slug-${slotProps.value?.slug}`" v-bind="slotProps"></slot>
                    <slot :name="`repeater-after-type-${slotProps.value?.type}`" v-bind="slotProps"></slot>

                    <template v-if="slotProps.value?.has_condition">
                        <div class="forms__field--repeater__condition">
                            <label class="text-sm">
                                {{ lang('fields::fields.field-editor.condition') }}
                            </label>
                            <div class="forms__field--repeater__condition__row">
                                <div>
                                    <AdminSelect
                                        v-model="slotProps.value.conditions[0]['field']"
                                        :options="
                                            [...internalModelValue].map((item) => ({
                                                id: item.slug,
                                                name: item.name,
                                            }))
                                        "
                                    ></AdminSelect>
                                </div>
                                <div>
                                    <AdminSelect
                                        v-model="slotProps.value.conditions[0]['comparison']"
                                        :options="[
                                            { id: '==', name: lang('fields::fields.comparison.==') },
                                            { id: '!=', name: lang('fields::fields.comparison.!=') },
                                            { id: '>', name: lang('fields::fields.comparison.>') },
                                            { id: '>=', name: lang('fields::fields.comparison.>=') },
                                            { id: '<', name: lang('fields::fields.comparison.<') },
                                            { id: '<=', name: lang('fields::fields.comparison.<=') },
                                            { id: 'in', name: lang('fields::fields.comparison.in') },
                                            { id: 'notin', name: lang('fields::fields.comparison.notin') },
                                        ]"
                                    ></AdminSelect>
                                </div>
                                <div>
                                    <AdminText v-model="slotProps.value.conditions[0]['value']" />
                                </div>
                            </div>
                        </div>
                    </template>
                </template>
            </component>
        </div>
        <div class="forms__field--repeater__footer__actions">
            <Button type="button" size="sm" dusk="repeater-add" @click="addToRepeater">
                <icon
                    :icon="props.plusIcon ?? 'hero/solid/plus-sm'"
                    class="forms__field--repeater__footer__icon"
                ></icon>
            </Button>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { toRefs, nextTick, ref, watch, computed, reactive } from 'vue';
import AdminRepeaterBlock from '@src/Fields/Pages/Repeater/AdminRepeaterBlock.vue';
import AdminRepeaterTable from '@src/Fields/Pages/Repeater/AdminRepeaterTable.vue';
import AdminRepeaterRow from '@src/Fields/Pages/Repeater/AdminRepeaterRow.vue';
import { arrayMove } from '@src/Common/Helpers/array';
import { v4 as uuidv4 } from 'uuid';
import type { FieldComponent } from '@src/Common/Components/types';

const modelValue = defineModel({
    type: Array,
});

if (typeof modelValue.value === 'undefined' || modelValue.value === null) {
    modelValue.value = [];
}

const internalModelValue = ref(
    modelValue.value
        ? [...modelValue.value].map((item) => reactive({
              conditions: [{ field: '', comparison: '', value: '' }],
              ...(typeof item === 'object' && item !== null ? item : {}),
              _id: uuidv4(),
          }))
        : [],
);

const emit = defineEmits(['update:modelValue']);

const props = withDefaults(defineProps<FieldComponent>(), {
    field: () => ({
        name: '',
        slug: '',
        type: 'text',
    }),
    tabindex: null,
    inline: false,
    multiple: false,
    size: 'base',
    minusIcon: 'hero/solid/minus-sm',
    plusIcon: 'hero/solid/plus-sm',
});

const { field, tabindex, inline, multiple } = toRefs(props);

const layoutRef = ref<InstanceType<typeof AdminRepeaterBlock> | null>(null);

const layoutComponent = computed(() => {
    switch (field.value.layout) {
        case 'table':
            return AdminRepeaterTable;
        case 'row':
            return AdminRepeaterRow;
        case 'block':
        default:
            return AdminRepeaterBlock;
    }
});

/**
 * Handle focus from label click - expand first item or add new one.
 *
 * @return void No return expected.
 */
const handleLabelFocus = async () => {
    if (internalModelValue.value.length > 0) {
        if (layoutRef.value?.expand) {
            layoutRef.value.expand(internalModelValue.value[0]._id);
        }
    } else {
        await addToRepeater();
        await nextTick();
        if (layoutRef.value?.expand) {
            layoutRef.value.expand(internalModelValue.value[0]._id);
        }
    }
};

/**
 * Used to add a new item to the repeater
 *
 * @return void No return expected.
 */
const addToRepeater = async () => {
    const newValue = reactive({
        _id: uuidv4(),
        conditions: [{ field: '', comparison: '==', value: '' }],
    });

    if (field.value.fields) {
        Object.keys(field.value.fields).forEach((subfield) => {
            newValue[subfield] = field.value.fields[subfield].default ?? null;
        });
    }

    internalModelValue.value.push(newValue);
    await nextTick();
};

/**
 * Used to remove an item from the repeater
 *
 * @param index number Index of the item to remove
 * @return void No return expected.
 */
const removeFromRepeater = async (index) => {
    internalModelValue.value.splice(index, 1);
    await nextTick();
};

/**
 * Triggered when the drag of item from the list stops
 *
 * @param event SortableEvent
 * @return void No return expected.
 */
const end = (event: any) => {
    if (event.from === event.to) {
        const content = arrayMove([...internalModelValue.value], event.oldIndex, event.newIndex);

        internalModelValue.value = content;
    } else {
        const content = [...internalModelValue.value];

        content.splice(event.oldIndex, 1);

        internalModelValue.value = content;
    }
};

/**
 * Triggered when an item is added to the list
 *
 * @param event SortableEvent
 * @param item any
 * @return void No return expected.
 */
const add = (event: any, item: any) => {
    const content = [...internalModelValue.value];

    content.splice(event.newIndex, 0, reactive(item));

    internalModelValue.value = content;

    event.item.parentNode.removeChild(event.item);
};

/**
 * When the internal list changes, update the model value
 */
watch(
    internalModelValue,
    (value) => {
        emit(
            'update:modelValue',
            value.map((item) => {
                const newItem = { ...item };

                delete newItem._id;

                return newItem;
            }),
        );
    },
    { deep: true },
);
</script>
